import { makeStyles } from "@material-ui/core";
import React, {
  useCallback,
  useState,
  useEffect,
  useMemo,
  useRef,
} from "react";
import { useHistory, useParams } from "react-router-dom";
import { Select } from "antd";
import { REPORT_FONT, useReportFonts } from "../reportFonts";
import {
  FILTERS_CONFIG,
  initFilterValues,
  isFilterActive,
  getDisplayValue,
  getDefaultValue,
  getActiveFilterCount,
  areVisibleFilterValuesEqual,
  HorizontalDateRangeFilter,
  FilterApplyButton,
} from "./GlobalFilterPanel";
import { useReportFilterOptions } from "./useReportFilterOptions";
import {
  applyCascadeDraftChange,
  pruneInvalidCascadeSelections,
  getFilterFieldOptions,
  toFilterSelectOptions,
  FILTER_SELECT_SEARCH_PROPS,
  GlobalMultiSelectFilter,
  buildCascadedDropdownOptions,
  expandAllFilterSelections,
} from "./filterRelationshipUtils";
import ExecutiveSummary from "./pl-by-program/ExecutiveSummary";
import OverviewSector from "./pl-by-program/OverviewSector";
import RevGMBridge from "./pl-by-program/RevGMBridge";
import GMRevBridge from "./pl-by-program/GMRevBridge";
import CurrentMonthActualsVsForecast from "./pl-by-program/CurrentMonthActualsVsForecast";
import ActualsVsForecastByDateRange from "./pl-by-program/ActualsVsForecastByDateRange";
import CurrentMonthActualsVsForecastByProj from "./pl-by-program/CurrentMonthActualsVsForecastByProj";
import ActualsVsForecastByDateRangeByProj from "./pl-by-program/ActualsVsForecastByDateRangeByProj";
import ForecastVersionsComparison from "./pl-by-program/ForecastVersionsComparison";
import CurrentMonthActualsVsBudget from "./pl-by-program/CurrentMonthActualsVsBudget";
import PLMonthlyTrend from "./pl-by-program/PLMonthlyTrend";
import PSRTrend from "./pl-by-program/PSRTrend";
import NextYearForecast from "./pl-by-program/NextYearForecast";
import LtmKpi from "./pl-by-program/LtmKpi";
import {
  deriveActualsThroughFromYear,
  monthNameToKey,
  MONTH_INDEX,
} from "./pl-by-program/plTimeFilterUtils";
import {
  defaultDateRange,
  formatDownloadFilename,
} from "./pl-by-program/actualsVsForecastByDateRangeUtils";
import { downloadPLByProgramAPI } from "../../../api/reports-api";
import Loading from "../../../common-components/Loading";
import BackButton from "../BackButton";
import { goToReportsCatalog } from "../reportNav";

import { TH_BG_BAND } from "../reportTableConfig";

const PAGE_BG = "#F5F5F0";

const DOWNLOAD_TAB_KEY = {
  cm_actuals_vs_forecast: "cm_actuals_vs_forecast",
  cm_actuals_vs_forecast_proj: "cm_actuals_vs_forecast_by_proj",
  cm_actuals_vs_budget: "cm_actuals_vs_budget",
  psr_trend: "psr_trend",
  actuals_vs_forecast_by_date_range: "actuals_vs_forecast_by_date_range",
  actuals_vs_forecast_by_proj_date_range:
    "actuals_vs_forecast_by_proj_date_range",
};
const TAB_ACTIVE = "#4F46E5";
const BORDER = "#E5E7EB";
const MUTED = "#6B7280";

const TABS = [
  {
    key: "executive_summary",
    label: "Executive Summary",
    component: ExecutiveSummary,
  },
  { key: "overview_sector", label: "Overview: BU", component: OverviewSector },
  { key: "rev_gm_bridge", label: "Rev/GM Bridge", component: RevGMBridge },
  { key: "gm_rev_bridge", label: "GM/Rev Bridge", component: GMRevBridge },
  {
    key: "cm_actuals_vs_forecast",
    label: "Current Month Actuals Vs Forecast",
    component: CurrentMonthActualsVsForecast,
  },
  {
    key: "actuals_vs_forecast_by_date_range",
    label: "Actuals Vs Forecast by Date Range",
    component: ActualsVsForecastByDateRange,
  },
  {
    key: "cm_actuals_vs_budget",
    label: "Current Month Actuals Vs Budget",
    component: CurrentMonthActualsVsBudget,
  },
  {
    key: "cm_actuals_vs_forecast_proj",
    label: "Current Month Actuals Vs Forecast — By Proj.",
    component: CurrentMonthActualsVsForecastByProj,
  },
  {
    key: "actuals_vs_forecast_by_proj_date_range",
    label: "Actuals Vs Forecast by Date Range — By Proj.",
    component: ActualsVsForecastByDateRangeByProj,
  },
  {
    key: "forecast_versions_comparison",
    label: "Forecast Versions — Comparison",
    component: ForecastVersionsComparison,
  },
  {
    key: "pl_monthly_trend",
    label: "P&L Monthly Trend",
    component: PLMonthlyTrend,
  },
  { key: "psr_trend", label: "PSR Trend", component: PSRTrend },
  {
    key: "next_year_forecast",
    label: "Next Year Forecast",
    component: NextYearForecast,
  },
  { key: "ltm_kpi", label: "LTM KPI", component: LtmKpi },
];

/* ── Per-tab visible filter keys (monthName + quarter on every tab) ── */
const TAB_FILTERS = {
  executive_summary: [
    "year",
    "monthName",
    "quarter",
    "contractType",
    "contractId",
    "clinLvl2",
    "orgId",
    "businessType",
    "awardType",
    "competitionType",
    "popEndDate",
  ],
  overview_sector: [
    "year",
    "monthName",
    "quarter",
    "orgId",
    "contractType",
    "clinLvl2",
    "businessType",
    "awardType",
    "competitionType",
    "popEndDate",
  ],
  rev_gm_bridge: [
    "year",
    "monthName",
    "quarter",
    "orgId",
    "contractType",
    "clinLvl2",
    "businessType",
    "awardType",
    "competitionType",
    "bu",
    "sector",
    "popEndDate",
  ],
  gm_rev_bridge: [
    "year",
    "monthName",
    "quarter",
    "orgId",
    "contractType",
    "clinLvl2",
    "businessType",
    "awardType",
    "competitionType",
    "bu",
    "sector",
    "popEndDate",
  ],
  cm_actuals_vs_forecast: [
    "year",
    "monthName",
    "quarter",
    "contractType",
    "contractId",
    "clinLvl2",
    "orgId",
    "businessType",
    "awardType",
    "competitionType",
    "bu",
    "popEndDate",
    "sector",
    "projectId",
    "contractName",
    "customerName",
  ],
  actuals_vs_forecast_by_date_range: [
    "contractType",
    "contractId",
    "clinLvl2",
    "orgId",
    "businessType",
    "awardType",
    "competitionType",
    "bu",
    "popEndDate",
    "sector",
    "projectId",
    "contractName",
    "customerName",
  ],
  actuals_vs_forecast_by_proj_date_range: [
    "contractType",
    "contractId",
    "clinLvl2",
    "orgId",
    "businessType",
    "awardType",
    "competitionType",
    "bu",
    "popEndDate",
    "sector",
    "projectId",
    "contractName",
    "customerName",
  ],
  cm_actuals_vs_forecast_proj: [
    "year",
    "monthName",
    "quarter",
    "orgId",
    "contractType",
    "clinLvl2",
    "contractId",
    "businessType",
    "awardType",
    "competitionType",
    "bu",
    "popEndDate",
    "sector",
    "contractName",
    "projectId",
    "customerName",
  ],
  forecast_versions_comparison: [
    "year",
    "monthName",
    "quarter",
    "contractType",
    "contractId",
    "clinLvl2",
    "orgId",
    "businessType",
    "awardType",
    "competitionType",
    "bu",
    "popEndDate",
  ],
  cm_actuals_vs_budget: [
    "year",
    "monthName",
    "quarter",
    "contractType",
    "contractId",
    "clinLvl2",
    "orgId",
    "businessType",
    "awardType",
    "competitionType",
    "bu",
    "popEndDate",
    "sector",
    "projectId",
  ],
  pl_monthly_trend: [
    "year",
    "monthName",
    "quarter",
    "contractType",
    "contractId",
    "clinLvl2",
    "orgId",
    "businessType",
    "awardType",
    "competitionType",
    "bu",
    "popEndDate",
    "sector",
    "projectId",
  ],
  psr_trend: [
    "year",
    "monthName",
    "quarter",
    "contractType",
    "contractId",
    "clinLvl2",
    "orgId",
    "businessType",
    "awardType",
    "competitionType",
    "bu",
    "popEndDate",
    "sector",
  ],
  next_year_forecast: [
    "year",
    "monthName",
    "quarter",
    "contractType",
    "contractId",
    "clinLvl2",
    "orgId",
    "businessType",
    "awardType",
    "competitionType",
    "bu",
    "popEndDate",
    "sector",
    "projectId",
    "contractName",
    "customerName",
  ],
  ltm_kpi: [
    "year",
    "monthName",
    "quarter",
    "sector",
    "orgId",
    "contractType",
    "contractId",
    "clinLvl2",
    "projectId",
    "businessType",
    "awardType",
    "competitionType",
    "bu",
    "popEndDate",
    "contractName",
    "customerName",
  ],
};

const useStyles = makeStyles(() => ({
  page: {
    backgroundColor: PAGE_BG,
    fontFamily: REPORT_FONT,
    display: "flex",
    flexDirection: "column",
    minHeight: "calc(100vh - 80px)",
  },

  topBar: {
    position: "sticky",
    top: 0,
    zIndex: 20,
    background: "#fff",
    padding: "12px 24px",
    display: "flex",
    alignItems: "center",
    gap: "12px",
    flexShrink: 0,
  },
  crumb: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    fontSize: 14,
    color: "#9CA3AF",
    minWidth: 0,
  },
  crumbLink: {
    cursor: "pointer",
    color: "#9CA3AF",
    "&:hover": { color: "#6B7280" },
  },
  crumbSep: { color: "#D1D5DB", userSelect: "none" },
  crumbCur: { color: "#111827", fontWeight: 500 },

  topActions: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    marginLeft: "auto",
    flexShrink: 0,
  },
  actionBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    height: 32,
    padding: "0 12px",
    borderRadius: 6,
    border: "1px solid #E5E7EB",
    background: "#fff",
    color: "#374151",
    fontSize: 13,
    fontWeight: 500,
    cursor: "pointer",
    transition: "background 0.15s, border-color 0.15s",
    "&:hover": { background: "#F9FAFB", borderColor: "#D1D5DB" },
  },
  actionBtnActive: {
    borderColor: TAB_ACTIVE,
    background: TAB_ACTIVE,
    color: "#fff",
    "&:hover": { background: "#4338CA", borderColor: "#4338CA" },
  },
  actionBtnIconOnly: {
    width: 32,
    minWidth: 32,
    padding: 0,
    justifyContent: "center",
  },
  filterBadge: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    minWidth: 18,
    height: 18,
    padding: "0 5px",
    borderRadius: 9,
    background: "rgba(255,255,255,0.25)",
    color: "#fff",
    fontSize: 11,
    fontWeight: 700,
    lineHeight: 1,
  },

  tabBar: {
    background: "#fff",
    borderBottom: `1px solid ${BORDER}`,
    display: "flex",
    flexWrap: "wrap",
    flexShrink: 0,
    padding: "0 24px",
    gap: "4px 0",
  },
  tab: {
    padding: "12px 16px",
    fontSize: 14,
    fontWeight: 500,
    color: "#374151",
    cursor: "pointer",
    whiteSpace: "nowrap",
    border: "none",
    borderBottom: "2px solid transparent",
    background: "none",
    marginBottom: -1,
    transition: "color 0.15s, border-color 0.15s",
    "&:hover": { color: TAB_ACTIVE },
  },
  tabActive: {
    color: TAB_ACTIVE,
    fontWeight: 700,
    borderBottom: `2px solid ${TAB_ACTIVE}`,
  },

  /* ── horizontal filter bar ── */
  hFilterBar: {
    position: "relative",
    background: "#d6dbf8",
    borderTop: "2px solid #191b8c",
    borderBottom: `1px solid ${BORDER}`,
    padding: "12px 52px 12px 24px",
    display: "flex",
    alignItems: "flex-end",
    flexWrap: "wrap",
    gap: 10,
    flexShrink: 0,
  },
  hFilterGroup: {
    display: "flex",
    flexDirection: "column",
    gap: 4,
    flexShrink: 0,
    background: "#fff",
    borderRadius: 8,
    padding: "6px 10px 8px",
    minWidth: 120,
  },
  hFilterLabel: {
    fontSize: 10,
    fontWeight: 700,
    color: "#191b8c",
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    whiteSpace: "nowrap",
  },
  hFilterInput: {
    height: 26,
    padding: "0 6px",
    border: "none",
    borderRadius: 4,
    fontSize: 12,
    fontFamily: REPORT_FONT,
    color: "#111827",
    outline: "none",
    minWidth: 110,
    background: "transparent",
    "&:focus": { outline: `1px solid ${TAB_ACTIVE}`, borderRadius: 4 },
  },
  hFilterActions: {
    display: "flex",
    alignItems: "flex-end",
    gap: 8,
    marginLeft: "auto",
    flexShrink: 0,
  },
  hFilterApply: {
    height: 34,
    padding: "0 16px",
    borderRadius: 6,
    border: "none",
    background: TAB_ACTIVE,
    color: "#fff",
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
    fontFamily: REPORT_FONT,
    whiteSpace: "nowrap",
    "&:hover": { background: "#4338CA" },
    "&:disabled": { opacity: 0.38, cursor: "not-allowed" },
    "&:disabled:hover": { background: "inherit" },
  },
  hFilterClearAll: {
    alignSelf: "flex-end",
    height: 34,
    padding: "0 12px",
    borderRadius: 6,
    border: `1px solid ${BORDER}`,
    background: "#fff",
    color: "#374151",
    fontSize: 13,
    fontWeight: 500,
    cursor: "pointer",
    flexShrink: 0,
    whiteSpace: "nowrap",
    fontFamily: REPORT_FONT,
    "&:hover": { color: TAB_ACTIVE, borderColor: TAB_ACTIVE },
    "&:disabled": { opacity: 0.38, cursor: "not-allowed" },
  },
  hFilterClose: {
    position: "absolute",
    top: 10,
    right: 10,
    width: 28,
    height: 28,
    borderRadius: "50%",
    border: "2px solid #191b8c",
    background: TH_BG_BAND,
    color: "#fff",
    fontSize: 13,
    fontWeight: 700,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontFamily: REPORT_FONT,
    "&:hover": { background: "#111827" },
  },

  /* ── active filter chips ── */
  chipsBar: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    padding: "8px 16px",
    background: "#F8F9FF",
    borderBottom: `1px solid ${BORDER}`,
    flexWrap: "wrap",
    flexShrink: 0,
  },
  chip: {
    display: "inline-flex",
    alignItems: "center",
    gap: 5,
    height: 26,
    padding: "0 10px",
    borderRadius: 20,
    background: TH_BG_BAND,
    border: `1px solid ${TH_BG_BAND}`,
    fontSize: 11,
    fontWeight: 500,
    color: "#F1F5F9",
    whiteSpace: "nowrap",
    flexShrink: 0,
  },
  chipLabel: { color: "#94A3B8", fontWeight: 400, marginRight: 2 },
  chipClear: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: 14,
    height: 14,
    borderRadius: "50%",
    background: "none",
    border: "none",
    cursor: "pointer",
    color: "#94A3B8",
    fontSize: 10,
    padding: 0,
    marginLeft: 2,
    "&:hover": { background: "rgba(255,255,255,0.15)", color: "#fff" },
    "&:disabled": { opacity: 0.38, cursor: "not-allowed" },
  },
  chipsClearAll: {
    fontSize: 11,
    fontWeight: 600,
    color: "#6B7280",
    background: "none",
    border: "none",
    cursor: "pointer",
    padding: "0 6px",
    marginLeft: 4,
    whiteSpace: "nowrap",
    flexShrink: 0,
    "&:hover": { color: TAB_ACTIVE },
    "&:disabled": { opacity: 0.38, cursor: "not-allowed" },
  },

  body: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  },
  content: {
    flex: 1,
    padding: "20px 16px",
    overflowY: "auto",
    minWidth: 0,
  },
}));

function FilterIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <line x1="4" y1="6" x2="20" y2="6" />
      <line x1="8" y1="12" x2="16" y2="12" />
      <line x1="10" y1="18" x2="14" y2="18" />
    </svg>
  );
}

function DownloadIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="7 10 12 15 17 10" />
      <line x1="12" y1="15" x2="12" y2="3" />
    </svg>
  );
}

function isOnlyDefaultYear(filterValues, vKeys) {
  const keys = vKeys
    ? vKeys.filter((k) => k !== "year")
    : FILTERS_CONFIG.filter((f) => f.key !== "year").map((f) => f.key);
  return (
    !keys.some((k) => {
      const f = FILTERS_CONFIG.find((fc) => fc.key === k);
      return f && isFilterActive(f, filterValues[k]);
    }) && String(filterValues.year) === "2026"
  );
}

/* ── Horizontal filter bar component ─────────────────────────── */
function HorizontalFilterBar({
  visibleKeys,
  draft,
  dropdownOptions,
  onChange,
  onClearAll,
  onApply,
  onClose,
  classes,
  clearAllDisabled,
  applyDisabled,
  activeTab,
}) {
  const filters = visibleKeys
    ? FILTERS_CONFIG.filter((f) => visibleKeys.includes(f.key))
    : FILTERS_CONFIG;

  const selectStyle = { minWidth: 110, maxWidth: 180, fontSize: 12 };
  const currentYear = new Date().getFullYear();
  /** LTM KPI: Month is single-select (API uses latest month as LTM anchor). */
  const monthIsSingleSelect = activeTab === "ltm_kpi";

  return (
    <div className={classes.hFilterBar}>
      {filters.map((f) => {
        let opts = toFilterSelectOptions(
          f.key,
          getFilterFieldOptions(f, dropdownOptions),
        );
        if (f.key === "year" && activeTab === "next_year_forecast") {
          opts = opts.filter((o) => Number(o.value) >= currentYear);
        }
        const val = draft[f.key];
        const asSelect =
          f.type === "select" || (monthIsSingleSelect && f.key === "monthName");
        const asMulti =
          f.type === "multiselect" &&
          !(monthIsSingleSelect && f.key === "monthName");

        const monthSingleValue = (() => {
          if (!Array.isArray(val) || val.length === 0) return "All";
          return val.reduce((a, b) => {
            const ak = monthNameToKey(a);
            const bk = monthNameToKey(b);
            return (MONTH_INDEX[ak] || 0) >= (MONTH_INDEX[bk] || 0) ? a : b;
          });
        })();

        return (
          <div key={f.key} className={classes.hFilterGroup}>
            <span className={classes.hFilterLabel}>{f.label}</span>

            {asSelect && (
              <Select
                size="small"
                style={selectStyle}
                value={
                  f.key === "monthName"
                    ? monthSingleValue
                    : val || (f.key === "year" ? opts[0]?.value : "All")
                }
                onChange={(v) => {
                  if (f.key === "monthName" && monthIsSingleSelect) {
                    onChange(f.key, v === "All" ? [] : [v]);
                    return;
                  }
                  onChange(f.key, v === "All" ? f.defaultValue : v);
                }}
                options={
                  f.key === "year"
                    ? opts
                    : [{ value: "All", label: "All" }, ...opts]
                }
                {...FILTER_SELECT_SEARCH_PROPS}
                popupMatchSelectWidth={false}
              />
            )}

            {asMulti && (
              <GlobalMultiSelectFilter
                size="small"
                style={selectStyle}
                value={val}
                options={opts}
                onChange={(v) => onChange(f.key, v)}
                {...FILTER_SELECT_SEARCH_PROPS}
                popupMatchSelectWidth={false}
              />
            )}

            {f.type === "text" && (
              <input
                className={classes.hFilterInput}
                value={val || ""}
                placeholder="All"
                onChange={(e) => onChange(f.key, e.target.value)}
              />
            )}
            {f.type === "daterange" && (
              <HorizontalDateRangeFilter
                value={val}
                onChange={(next) => onChange(f.key, next)}
              />
            )}
          </div>
        );
      })}

      <div className={classes.hFilterActions}>
        <button
          className={classes.hFilterClearAll}
          onClick={onClearAll}
          disabled={clearAllDisabled}
        >
          Clear All
        </button>
        <FilterApplyButton
          draft={draft}
          onApply={onApply}
          className={classes.hFilterApply}
          disabled={applyDisabled}
        >
          Apply
        </FilterApplyButton>
      </div>

      <button
        className={classes.hFilterClose}
        onClick={onClose}
        title="Close Filters"
      >
        ✕
      </button>
    </div>
  );
}

export default function PLByProgram() {
  const classes = useStyles();
  const history = useHistory();
  const { tab: tabParam } = useParams();
  useReportFonts();

  const visibleTabs = TABS;

  const activeTab =
    visibleTabs.find((t) => t.key === tabParam)?.key ?? visibleTabs[0].key;
  const [filterOpen, setFilterOpen] = useState(true);
  const [byProjParams, setByProjParams] = useState({
    version: "q1",
    key: ["contractRevenue"],
  });
  /* Range picker lives inside ActualsVsForecastByDateRange itself — it reports
     the currently-selected range back up via onParamsChange so Download can use it. */
  const [dateRangeParams, setDateRangeParams] = useState(defaultDateRange);
  const [dateRangeByProjParams, setDateRangeByProjParams] = useState({
    ...defaultDateRange(),
    key: ["contractRevenue"],
  });
  const [downloading, setDownloading] = useState(false);

  /* Per-tab filter state — each tab has its own independent applied/draft values */
  const [tabApplied, setTabApplied] = useState({});
  const [tabDraft, setTabDraft] = useState({});

  /* Stable default — created once so render-path fallback doesn't change reference each render */
  const defaultFilters = useMemo(() => initFilterValues(), []); // eslint-disable-line react-hooks/exhaustive-deps

  /* Always reflects the active tab's filters; falls back to defaults on first visit */
  const appliedFilters = tabApplied[activeTab] ?? defaultFilters;
  const draftFilters = tabDraft[activeTab] ?? defaultFilters;

  /* Stable setters — read activeTab from a ref so downstream useCallbacks stay stable */
  const activeTabRef = useRef(activeTab);
  useEffect(() => {
    activeTabRef.current = activeTab;
  }, [activeTab]);

  const setAppliedFilters = useCallback((updater) => {
    const tab = activeTabRef.current;
    setTabApplied((prev) => ({
      ...prev,
      [tab]:
        typeof updater === "function"
          ? updater(prev[tab] ?? initFilterValues())
          : updater,
    }));
  }, []);

  const setDraftFilters = useCallback((updater) => {
    const tab = activeTabRef.current;
    setTabDraft((prev) => ({
      ...prev,
      [tab]:
        typeof updater === "function"
          ? updater(prev[tab] ?? initFilterValues())
          : updater,
    }));
  }, []);

  const visibleKeys = TAB_FILTERS[activeTab] || null;
  const { dropdownOptions, relationshipRows, baseOptions } =
    useReportFilterOptions(draftFilters, visibleKeys);
  const appliedDropdownOptions = useMemo(
    () =>
      buildCascadedDropdownOptions(
        relationshipRows,
        baseOptions,
        appliedFilters,
        visibleKeys,
      ),
    [relationshipRows, baseOptions, appliedFilters, visibleKeys],
  );

  /* When switching to Next Year Forecast, reset year to current year if a past year is selected */
  useEffect(() => {
    if (activeTab !== "next_year_forecast") return;
    const cy = String(new Date().getFullYear());
    if (Number(appliedFilters.year) < Number(cy)) {
      setAppliedFilters((prev) => ({ ...prev, year: cy }));
      setDraftFilters((prev) => ({ ...prev, year: cy }));
    }
  }, [activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  /* LTM KPI Month is single-select — keep only the latest month if several were carried over */
  useEffect(() => {
    if (activeTab !== "ltm_kpi") return;
    const months = appliedFilters.monthName;
    if (!Array.isArray(months) || months.length <= 1) return;
    const latest = months.reduce((a, b) => {
      const ak = monthNameToKey(a);
      const bk = monthNameToKey(b);
      return (MONTH_INDEX[ak] || 0) >= (MONTH_INDEX[bk] || 0) ? a : b;
    });
    const next = [latest];
    setAppliedFilters((prev) => ({ ...prev, monthName: next }));
    setDraftFilters((prev) => ({ ...prev, monthName: next }));
  }, [activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  const actualsThroughDate = useMemo(() => deriveActualsThroughFromYear(), []);

  /** Only pass filters visible on the active tab — hidden values stay in state for when user returns */
  const effectiveFilters = useMemo(() => {
    const visible = new Set(TAB_FILTERS[activeTab] || []);
    const next = { ...appliedFilters };
    FILTERS_CONFIG.forEach((f) => {
      if (!visible.has(f.key)) next[f.key] = getDefaultValue(f);
    });
    return expandAllFilterSelections(next, appliedDropdownOptions);
  }, [appliedFilters, activeTab, appliedDropdownOptions]);

  /* Sync draft when filter bar opens */
  useEffect(() => {
    if (filterOpen) {
      setDraftFilters(
        pruneInvalidCascadeSelections(
          appliedFilters,
          relationshipRows,
          visibleKeys,
        ),
      );
    }
  }, [filterOpen, appliedFilters, relationshipRows, visibleKeys]);
  const filterCount = getActiveFilterCount(appliedFilters, visibleKeys);

  const activeChips = (
    visibleKeys
      ? FILTERS_CONFIG.filter((f) => visibleKeys.includes(f.key))
      : FILTERS_CONFIG
  ).filter((f) => isFilterActive(f, appliedFilters[f.key]));

  const clearChip = useCallback((f) => {
    setAppliedFilters((prev) => ({ ...prev, [f.key]: getDefaultValue(f) }));
    setDraftFilters((prev) => ({ ...prev, [f.key]: getDefaultValue(f) }));
  }, []);

  const clearAllApplied = useCallback(() => {
    setAppliedFilters((prev) => {
      const next = { ...prev };
      (visibleKeys || FILTERS_CONFIG.map((f) => f.key)).forEach((k) => {
        const f = FILTERS_CONFIG.find((fc) => fc.key === k);
        if (f) next[k] = getDefaultValue(f);
      });
      return next;
    });
    setDraftFilters((prev) => {
      const next = { ...prev };
      (visibleKeys || FILTERS_CONFIG.map((f) => f.key)).forEach((k) => {
        const f = FILTERS_CONFIG.find((fc) => fc.key === k);
        if (f) next[k] = getDefaultValue(f);
      });
      return next;
    });
  }, [visibleKeys]);

  const handleDraftChange = useCallback(
    (key, value) => {
      setDraftFilters((prev) =>
        applyCascadeDraftChange(
          prev,
          key,
          value,
          relationshipRows,
          visibleKeys,
        ),
      );
    },
    [relationshipRows, visibleKeys],
  );

  const handleApply = useCallback(() => {
    setAppliedFilters(
      pruneInvalidCascadeSelections(
        draftFilters,
        relationshipRows,
        visibleKeys,
      ),
    );
  }, [draftFilters, relationshipRows, visibleKeys]);

  const clearAll = useCallback(() => {
    setDraftFilters((prev) => {
      const next = { ...prev };
      (visibleKeys || FILTERS_CONFIG.map((f) => f.key)).forEach((k) => {
        const f = FILTERS_CONFIG.find((fc) => fc.key === k);
        if (f) next[k] = getDefaultValue(f);
      });
      return next;
    });
  }, [visibleKeys]);

  const ActiveComponent = visibleTabs.find(
    (t) => t.key === activeTab,
  )?.component;
  const goBack = useCallback(() => goToReportsCatalog(history), [history]);

  const isDownloadable = activeTab in DOWNLOAD_TAB_KEY;

  const handleDownload = useCallback(async () => {
    if (!isDownloadable || downloading) return;
    const apiKey = DOWNLOAD_TAB_KEY[activeTab];
    const isDateRange = activeTab === "actuals_vs_forecast_by_date_range";
    const isDateRangeByProj =
      activeTab === "actuals_vs_forecast_by_proj_date_range";
    const params =
      activeTab === "cm_actuals_vs_forecast_proj"
        ? byProjParams
        : isDateRange
          ? dateRangeParams
          : isDateRangeByProj
            ? dateRangeByProjParams
            : {};
    setDownloading(true);
    try {
      const blob = await downloadPLByProgramAPI(
        apiKey,
        effectiveFilters,
        params,
      );
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = isDateRange
        ? formatDownloadFilename(
            dateRangeParams.startMonth,
            dateRangeParams.startYear,
            dateRangeParams.endMonth,
            dateRangeParams.endYear,
          )
        : isDateRangeByProj
          ? formatDownloadFilename(
              dateRangeByProjParams.startMonth,
              dateRangeByProjParams.startYear,
              dateRangeByProjParams.endMonth,
              dateRangeByProjParams.endYear,
            )
          : `PnL_${apiKey}_FY${effectiveFilters.year ?? ""}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("P&L download failed:", err);
    } finally {
      setDownloading(false);
    }
  }, [
    isDownloadable,
    downloading,
    activeTab,
    byProjParams,
    dateRangeParams,
    dateRangeByProjParams,
    effectiveFilters,
  ]);

  return (
    <div className={classes.page}>
      {downloading && <Loading />}
      {/* ── Top bar ── */}
      <div className={classes.topBar}>
        <BackButton onClick={goBack} ariaLabel="Back to Reports" />
        <nav className={classes.crumb}>
          <span className={classes.crumbLink} onClick={goBack}>
            Reports
          </span>
          <span className={classes.crumbSep}>&gt;</span>
          <span className={classes.crumbCur}>P&L by Program</span>
        </nav>
        <div className={classes.topActions}>
          {isDownloadable && (
            <button
              type="button"
              className={`${classes.actionBtn} ${classes.actionBtnIconOnly} ${classes.actionBtnActive}`}
              title="Download as Excel"
              aria-label="Download Report"
              onClick={handleDownload}
              disabled={downloading}
            >
              <DownloadIcon />
            </button>
          )}
          <button
            type="button"
            className={`${classes.actionBtn} ${filterOpen || filterCount > 0 ? classes.actionBtnActive : ""}`}
            onClick={() => setFilterOpen((p) => !p)}
          >
            <FilterIcon />
            Filters
            {filterCount > 0 && (
              <span className={classes.filterBadge}>{filterCount}</span>
            )}
          </button>
        </div>
      </div>

      {/* ── Tab bar ── */}
      <div className={classes.tabBar}>
        {visibleTabs.map((tab) => (
          <button
            key={tab.key}
            type="button"
            className={`${classes.tab} ${activeTab === tab.key ? classes.tabActive : ""}`}
            onClick={() =>
              history.replace(`/projects/reports/pl_by_program/${tab.key}`)
            }
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* ── Horizontal filter bar ── */}
      {filterOpen && (
        <HorizontalFilterBar
          visibleKeys={visibleKeys}
          draft={draftFilters}
          dropdownOptions={dropdownOptions}
          onChange={handleDraftChange}
          onClearAll={clearAll}
          onApply={handleApply}
          applyDisabled={areVisibleFilterValuesEqual(
            draftFilters,
            appliedFilters,
            visibleKeys,
          )}
          onClose={() => setFilterOpen(false)}
          classes={classes}
          clearAllDisabled={isOnlyDefaultYear(draftFilters, visibleKeys)}
          activeTab={activeTab}
        />
      )}

      {/* ── Active filter chips (shown when filter bar is hidden) ── */}
      {!filterOpen && activeChips.length > 0 && (
        <div className={classes.chipsBar}>
          {activeChips.map((f) => {
            const val = appliedFilters[f.key];
            const tip =
              f.type === "multiselect" && Array.isArray(val) && val.length > 1
                ? `${f.label}: ${val.join(", ")}`
                : `${f.label}: ${getDisplayValue(f, val)}`;
            return (
              <span key={f.key} className={classes.chip} title={tip}>
                <span className={classes.chipLabel}>{f.label}:</span>
                {getDisplayValue(f, val)}
                <button
                  className={classes.chipClear}
                  onClick={() => clearChip(f)}
                  title={`Clear ${f.label}`}
                  disabled={f.key === "year" && String(val) === "2026"}
                >
                  ✕
                </button>
              </span>
            );
          })}
          <button
            className={classes.chipsClearAll}
            onClick={clearAllApplied}
            disabled={isOnlyDefaultYear(appliedFilters, visibleKeys)}
          >
            Clear All
          </button>
        </div>
      )}

      {/* ── Content ── */}
      <div className={classes.body}>
        <div className={classes.content}>
          {ActiveComponent && (
            <ActiveComponent
              filters={effectiveFilters}
              actualsThroughDate={actualsThroughDate}
              {...(activeTab === "cm_actuals_vs_forecast_proj" && {
                onParamsChange: setByProjParams,
              })}
              {...(activeTab === "actuals_vs_forecast_by_date_range" && {
                onParamsChange: setDateRangeParams,
              })}
              {...(activeTab === "actuals_vs_forecast_by_proj_date_range" && {
                onParamsChange: setDateRangeByProjParams,
              })}
            />
          )}
        </div>
      </div>
    </div>
  );
}
